import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertOctagon, ShieldCheck } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { CARD, Label } from "@/components/common";
import { DOT, StatusPill, daysTxt, useComp } from "./compUtils";

function Kpi({ label, value, tone, testid, sub }) {
  return (
    <div className={`${CARD} p-4`} data-testid={testid}>
      <Label>{label}</Label>
      <p className={`mt-1 font-display text-3xl font-black ${tone || "text-gray-900"}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400">{sub}</p>}
    </div>
  );
}

export function CompDashboard({ onOpen, tick }) {
  const m = useComp();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/compliance/dashboard").then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e))); }, [tick]);
  if (!d) return null;
  const k = d.kpis;
  return (
    <div className="space-y-4" data-testid="comp-dash">
      {d.critical_nc.length > 0 && (
        <div className="rounded-3xl border border-rose-200 bg-rose-50 p-4" data-testid="comp-critical-nc">
          <p className="flex items-center gap-2 font-semibold text-rose-700"><AlertOctagon className="h-5 w-5" />{d.critical_nc.length} non-conformité(s) critique(s) ouverte(s)</p>
          {d.critical_nc.map((n) => <p key={n.id} className="mt-1 text-sm text-rose-800">{n.number} — {n.description}{n.due_date && ` · limite ${fmtDate(n.due_date)}`}</p>)}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Obligations actives" value={k.active} testid="ck-active" sub={`${k.a_qualifier} à qualifier · ${k.non_applicable} non applicable(s)`} />
        <Kpi label="Vérifiées et à jour" value={k.verified} tone="text-emerald-700" testid="ck-verified" sub={`${k.a_verifier} réalisation(s) à vérifier`} />
        <Kpi label="À venir (30 jours)" value={k.due_30} tone="text-amber-600" testid="ck-due30" />
        <Kpi label="En retard" value={k.late} tone={k.late ? "text-rose-600" : ""} testid="ck-late" sub={k.non_conforme ? `${k.non_conforme} non conforme(s)` : ""} />
        <Kpi label="Documents expirés" value={k.expired_docs} tone={k.expired_docs ? "text-rose-600" : ""} testid="ck-expired" />
        <Kpi label="Non-conformités ouvertes" value={k.open_nc} tone={k.open_nc ? "text-rose-600" : ""} testid="ck-nc" />
        <Kpi label="Actions correctives en attente" value={k.pending_actions} tone={k.pending_actions ? "text-amber-600" : ""} testid="ck-actions" />
        <div className={`${CARD} p-4`} data-testid="ck-colors"><Label>Répartition</Label>
          <div className="mt-2 space-y-1 text-sm">{[["green", "Vérifiée et à jour"], ["orange", "Échéance proche / action attendue"], ["red", "Dépassée ou non conforme"], ["grey", "À qualifier ou non applicable"]].map(([c, l]) => (
            <div key={c} className="flex items-center justify-between"><span className="flex items-center gap-2"><span className={`h-2.5 w-2.5 rounded-full ${DOT[c]}`} />{l}</span><b>{d.colors[c]}</b></div>))}</div></div>
      </div>
      <p className="flex items-start gap-2 text-xs text-gray-400"><ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />Ces indicateurs reflètent les vérifications enregistrées. Ils ne constituent pas une attestation de conformité du cabinet.</p>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className={`${CARD} p-4 sm:p-6`} data-testid="comp-next10">
          <Label className="mb-2">10 prochaines échéances</Label>
          {d.next.length === 0 ? <p className="text-sm text-gray-400">Aucune échéance planifiée. Qualifiez les obligations du registre pour les dater.</p> : d.next.map((o) => (
            <button type="button" key={o.id} onClick={() => onOpen(o.id)} data-testid={`next-${o.id}`} className="flex w-full items-center justify-between gap-2 border-b border-gray-50 py-2 text-left text-sm hover:bg-gray-50">
              <span className="min-w-0"><span className="block truncate font-medium">{o.title}</span><span className="text-xs text-gray-400">{m.families[o.family]}{o.responsible_name ? ` · ${o.responsible_name}` : ""}</span></span>
              <span className="shrink-0 text-right text-xs"><StatusPill ob={o} /><span className="block text-gray-500">{fmtDate(o.next_due)} · {daysTxt(o.days)}</span></span>
            </button>))}
        </div>
        <div className={`${CARD} p-4 sm:p-6`} data-testid="comp-by-resp">
          <Label className="mb-2">Vue par responsable</Label>
          <div className="space-y-1">{d.by_responsible.map((r) => (
            <div key={r.name} className="flex items-center justify-between border-b border-gray-50 py-2 text-sm">
              <span className="font-medium">{r.name}</span>
              <span className="flex gap-2 text-xs"><span className="text-gray-500">{r.total} au total</span>{r.late > 0 && <b className="text-rose-600">{r.late} en retard</b>}{r.soon > 0 && <b className="text-amber-600">{r.soon} à venir</b>}<span className="text-emerald-700">{r.ok} à jour</span>{r.a_qualifier > 0 && <span className="text-gray-400">{r.a_qualifier} à qualifier</span>}</span>
            </div>))}</div>
        </div>
      </div>
    </div>
  );
}
